import { cn } from '@/shared/lib';
import { FC, useMemo } from 'react';
import { useRTL } from '@/shared/hooks';
import { useTranslation } from 'react-i18next';
import { Typography } from '@/shared/components';
import { ServiceTypeEnum } from '@/app/store/api/restApi';
import { ChevronRightIcon, ChevronLeftIcon, Map, MapPin, Box } from 'lucide-react';
import { selectIsMinimalistic, useAppSelector } from '@/app/store';

interface AddressViewProps {
  error: boolean;
  value?: string;
  isCatalog?: boolean;
  onClick: VoidFunction;
  onClickMap?: VoidFunction;
  onlyDelivery?: boolean;
  onlyOrderPoint?: boolean;
  deliveryType?: ServiceTypeEnum;
}

export const AddressView: FC<AddressViewProps> = ({
  error,
  value,
  onClick,
  onClickMap,
  isCatalog = false,
  onlyDelivery = false,
  onlyOrderPoint = false,
  deliveryType = ServiceTypeEnum.Delivery,
}) => {
  const isRTL = useRTL();
  const { t } = useTranslation('catalog');
  const isMinimalistic = useAppSelector(selectIsMinimalistic);

  const ChevronIcon = isRTL ? ChevronLeftIcon : ChevronRightIcon;

  const typographyValue = useMemo(() => {
    if (!value || value === '') {
      if (deliveryType === ServiceTypeEnum.Takeout) return t('placeholders.pickupAddress');
      if (onlyDelivery) return t('placeholders.deliveryAddress');
      if (onlyOrderPoint) return t('placeholders.orderPointAddress');
      return t('placeholders.choiceDelivery');
    } else {
      if (deliveryType === ServiceTypeEnum.OrderPoint)
        return isRTL ? `${value}: ${t('orderPoints.cdek')}` : `${t('orderPoints.cdek')}: ${value}`;
      else return value;
    }
  }, [deliveryType, isRTL, onlyDelivery, onlyOrderPoint, t, value]);

  return (
    <div
      onClick={onClick}
      className={cn(
        'h-10 pr-5 flex w-full items-center rounded-md',
        !isCatalog && 'shadow-sm border border-solid bg-background pr-3',
        error && 'border-destructive',
        isMinimalistic && 'rounded-none shadow-none',
      )}
    >
      <div className={cn('flex items-center flex-1 h-full')}>
        {!isCatalog && onClickMap && (
          <>
            <div
              className={cn(
                'flex items-center gap-[2px] h-full pl-3',
                value || deliveryType !== ServiceTypeEnum.Takeout ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
              )}
              onClick={(e) => {
                e.stopPropagation();
                if (!value && deliveryType === ServiceTypeEnum.Takeout) return;
                onClickMap();
              }}
            >
              {deliveryType === ServiceTypeEnum.Takeout ? (
                <Map size={16} className='text-analog' />
              ) : deliveryType === ServiceTypeEnum.Delivery ? (
                <MapPin size={16} className='text-analog' />
              ) : (
                <Box size={16} className='text-analog' />
              )}
              <ChevronIcon size={16} className='flex-shrink-0 text-muted-foreground' />
            </div>
            <div className='h-5 w-[1px] bg-border mx-1' />
          </>
        )}

        <Typography
          variant={isCatalog ? 'text1' : 'text2'}
          color={
            value
              ? 'default'
              : !onlyDelivery && deliveryType !== ServiceTypeEnum.Takeout
                ? 'primary'
                : isCatalog
                  ? 'default'
                  : 'mutedForeground'
          }
          className={cn('line-clamp-1 break-all flex-1 cursor-pointer', isRTL && 'text-right')}
        >
          {typographyValue}
        </Typography>
      </div>
    </div>
  );
};
